import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Flex,
  Badge,
  Button,
  Spinner,
  Stack,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  Icon,
  Checkbox,
  useColorModeValue
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { MdCheck, MdClose } from 'react-icons/md';
import { FiEye } from 'react-icons/fi';
import GroupingModel from 'models/grouping/GroupingModel';
import { BasicItemCard, ListingGrid } from 'components';
import { NavigationService } from 'services';
import { RiAuctionLine } from 'react-icons/ri';

export interface DetailedGroupingCardProps {
  grouping: GroupingModel;
  onApprove?: (groupingId: string) => void;
  onReject?: (groupingId: string) => void;
  onRemoveListing?: (groupingId: string, listingId: string) => void;
  approveLoading?: boolean;
  rejectLoading?: boolean;
  defaultAccordionOpen?: boolean;
  selectionMode?: boolean;
  selected?: boolean;
  onSelect?: () => void;
}

export default function DetailedGroupingCard({
  grouping,
  onApprove,
  onReject,
  onRemoveListing,
  approveLoading = false,
  rejectLoading = false,
  defaultAccordionOpen = false,
  selectionMode = false,
  selected = false,
  onSelect
}: DetailedGroupingCardProps) {
  const navigate = useNavigate();
  const selectedBgColor = useColorModeValue('blue.50', 'blue.900');
  const selectedBorderColor = useColorModeValue('blue.400', 'blue.500');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700');

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'yellow';
      case 'approved':
        return 'green';
      case 'rejected':
        return 'red';
      default:
        return 'gray';
    }
  };

  const handleApprove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onApprove && !approveLoading && !rejectLoading) {
      onApprove(grouping.id);
    }
  };

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onReject && !approveLoading && !rejectLoading) {
      onReject(grouping.id);
    }
  };

  const handleItemClick = () => {
    navigate(NavigationService.getItemDetailsPath(grouping.item.id, grouping.item.getDisplayName()));
  };

  const handleArtistClick = () => {
    navigate(NavigationService.getArtistDetailsPath(grouping.item.artistId, grouping.item.getArtistName()));
  };

  const handleListingNameClick = (id: string, name: string) => {
    navigate(NavigationService.getListingDetailsPath(id, name));
  };

  const handleListingArtistClick = (id: string, name: string) => {
    navigate(NavigationService.getArtistDetailsPath(id, name));
  };

  const handleViewGrouping = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(NavigationService.getGroupingDetailsPath(grouping.id));
  };

  const handleRemoveListing = (listingId: string) => {
    if (onRemoveListing) {
      onRemoveListing(grouping.id, listingId);
    }
  };

  const handleCardClick = () => {
    if (selectionMode && onSelect) {
      onSelect();
    }
  };

  return (
    <Card
      variant="outline"
      borderRadius="lg"
      cursor={selectionMode ? 'pointer' : 'default'}
      onClick={handleCardClick}
      borderColor={selected ? selectedBorderColor : undefined}
      borderWidth={selected ? '2px' : '1px'}
      bg={selected ? selectedBgColor : undefined}
      _hover={selectionMode ? { bg: selected ? selectedBgColor : hoverBgColor } : undefined}
      transition="all 0.2s"
    >
      <CardHeader borderBottomWidth="1px">
        <Flex direction={{ base: 'column', md: 'row' }} align={{ base: 'flex-start', md: 'center' }} justify="space-between" gap={4}>
          <Stack spacing={1} alignItems="flex-start">
            <HStack spacing={3}>
              {selectionMode && (
                <Checkbox
                  isChecked={selected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onSelect?.();
                  }}
                  size="lg"
                  colorScheme="blue"
                />
              )}
              <Badge colorScheme={getStatusColor(grouping.status)}>{grouping.status}</Badge>
            </HStack>
          </Stack>
          <HStack spacing={2}>
            {!selectionMode && (
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FiEye />}
                onClick={handleViewGrouping}
              >
                View
              </Button>
            )}
            {!selectionMode && grouping.status.toLowerCase() === 'pending' && (
              <>
                {onApprove && (
                  <Button
                    colorScheme="green"
                    size="sm"
                    leftIcon={approveLoading ? <Spinner size="xs" /> : <MdCheck />}
                    isLoading={approveLoading}
                    isDisabled={rejectLoading}
                    onClick={handleApprove}
                  >
                    Approve
                  </Button>
                )}
                {onReject && (
                  <Button
                    colorScheme="red"
                    size="sm"
                    leftIcon={rejectLoading ? <Spinner size="xs" /> : <MdClose />}
                    isLoading={rejectLoading}
                    isDisabled={approveLoading}
                    onClick={handleReject}
                  >
                    Reject
                  </Button>
                )}
              </>
            )}
          </HStack>
        </Flex>
      </CardHeader>
      <CardBody>
        <Stack gap={0}>
          <Box>
            <Heading fontSize="sm" mb={3}>Item</Heading>
            <BasicItemCard
              item={grouping.item}
              showMetadata={true}
              onItemClick={handleItemClick}
              onArtistClick={handleArtistClick}
            />
          </Box>
          <Box>
            <Accordion allowToggle defaultIndex={defaultAccordionOpen ? [0] : []}>
              <AccordionItem border="1px" borderColor="gray.200" borderRadius="md">
                <h2>
                  <AccordionButton>
                    <Flex flex="1" textAlign="left" flexDirection="row" gap={1} alignItems="center" py={2}>
                      <Icon as={RiAuctionLine} w={3} h={3} />
                      <Heading fontSize="sm">
                        Tagged Listings ({grouping.listings.length})
                      </Heading>
                    </Flex>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <ListingGrid
                    gridListings={grouping.listings}
                    isLoading={false}
                    showFilters={false}
                    onListingNameClick={handleListingNameClick}
                    onListingArtistClick={handleListingArtistClick}
                    onRemoveListingClick={onRemoveListing ? handleRemoveListing : undefined}
                  />
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>
        </Stack>
      </CardBody>
    </Card>
  );
}
